import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  CalendarDays,
  Clock,
  MapPin,
  Ticket,
  Users,
  Plus,
  Pencil,
  Trash2,
  Copy,
  List,
  QrCode,
  ExternalLink,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND = process.env.REACT_APP_BACKEND_URL
  ? process.env.REACT_APP_BACKEND_URL.replace('/api', '').replace(':8000', ':3000')
  : window.location.origin;

const TC_TEMPLATE = `TICKET & EVENT TERMS AND CONDITIONS

By purchasing a ticket to this event you agree to the following terms and conditions. Please read them carefully before completing your booking.

1. TICKET PURCHASE
Tickets are sold subject to these terms and conditions. Your booking is confirmed only once you receive a confirmation email with your QR code(s). Tickets remain the property of the event organiser.

2. ADMISSION
A valid QR code ticket must be presented at the door (either on screen or printed). Entry may be refused if the QR code cannot be scanned or has already been used. The organiser reserves the right to refuse entry to any person whose behaviour is deemed inappropriate, aggressive or dangerous, without refund.

3. AGE RESTRICTIONS
This event is strictly 18+. Valid photographic ID (passport, driving licence or PASS-accredited card) will be required on entry. No exceptions will be made. Guests who cannot provide valid ID will be refused entry without refund.

4. CHANGE OF MIND — NO COOLING-OFF PERIOD
In accordance with Regulation 28(1)(h) of the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013, the 14-day cooling-off period that applies to most online purchases does NOT apply to event tickets for a specific date. You do not have a statutory right to cancel your booking simply because you change your mind.

5. REFUNDS — ORGANISER CANCELLATION
If we cancel this event for any reason, you are entitled to a full refund of the ticket price paid. Refunds will be processed automatically to your original payment method within 14 days of the cancellation announcement.

6. REFUNDS — POSTPONEMENT OR MATERIAL CHANGE
If the event is postponed to a new date, or there is a material change (e.g. significant change of venue or programme), we will notify you and offer a refund. Refunds must be requested within 14 days of our notification. After this window, tickets will be treated as valid for the revised event.

7. REFUNDS — CHANGE OF MIND
We do not offer refunds for change of mind, inability to attend, or personal circumstances beyond our control (including illness, travel disruption or work commitments). Tickets are non-refundable once purchased except as required by law.

8. TICKET TRANSFERS
Tickets may be transferred to another person at your discretion. The named ticket holder is responsible for ensuring the person attending is aware of these terms. Tickets may not be resold for a value above their face price.

9. FORCE MAJEURE
In the event of cancellation due to circumstances beyond our reasonable control (including but not limited to extreme weather, government restrictions, venue unavailability or acts of God), we will endeavour to reschedule. Where rescheduling is not possible, a refund of the ticket face value will be offered. We shall not be liable for any additional losses (e.g. travel or accommodation costs).

10. PHOTOGRAPHY & RECORDING
[Amend as appropriate — e.g. "Photography for personal use is permitted. Commercial photography, video recording or audio recording without prior written consent from the organiser is strictly prohibited."]

11. DATA PROTECTION
Your personal data (name, email address, phone number) is collected for the purpose of processing your booking and communicating event information. Data is processed lawfully under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. Your data will not be shared with third parties except as required to process payment (Stripe) or deliver event communications. You may request access to, correction of, or deletion of your data by contacting us directly.

12. LIABILITY
To the fullest extent permitted by law, the event organiser's liability is limited to the face value of the ticket purchased. We are not liable for any indirect or consequential loss arising from attendance at, or inability to attend, this event.

13. GOVERNING LAW
These terms and conditions are governed by and construed in accordance with the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.

14. CONTACT
For any queries regarding your booking or these terms, please contact us directly.`;

const EMPTY_FORM = {
  name: '',
  description: '',
  date: '',
  time: '',
  end_time: '',
  location: '',
  price_per_ticket: '0',
  capacity: '',
  image_url: '',
  terms_and_conditions: '',
};

function EventFormDialog({ open, onClose, initial, onSaved }) {
  const { token } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              name: initial.name || '',
              description: initial.description || '',
              date: initial.date || '',
              time: initial.time || '',
              end_time: initial.end_time || '',
              location: initial.location || '',
              price_per_ticket: String(initial.price_per_ticket ?? '0'),
              capacity: String(initial.capacity || ''),
              image_url: initial.image_url || '',
              terms_and_conditions: initial.terms_and_conditions || '',
            }
          : EMPTY_FORM
      );
    }
  }, [open, initial]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Event name is required'); return; }
    if (!form.date) { toast.error('Date is required'); return; }
    if (!form.time) { toast.error('Time is required'); return; }
    if (!form.capacity || parseInt(form.capacity) < 1) { toast.error('Capacity must be at least 1'); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        date: form.date,
        time: form.time,
        end_time: form.end_time.trim() || null,
        location: form.location.trim() || null,
        price_per_ticket: parseFloat(form.price_per_ticket) || 0,
        capacity: parseInt(form.capacity),
        image_url: form.image_url.trim() || null,
        terms_and_conditions: form.terms_and_conditions.trim() || null,
      };
      if (isEdit) {
        await axios.put(`${API}/admin/events/${initial.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/admin/events`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Event created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const isFree = parseFloat(form.price_per_ticket || '0') === 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Event' : 'Create Event'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>Event Name *</Label>
            <Input value={form.name} onChange={set('name')} placeholder="e.g. Summer BBQ Night" />
          </div>

          <div className="space-y-1">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={set('description')} placeholder="Tell guests what to expect…" rows={3} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Date *</Label>
              <Input type="date" value={form.date} onChange={set('date')} />
            </div>
            <div className="space-y-1">
              <Label>Start time *</Label>
              <Input type="time" value={form.time} onChange={set('time')} />
            </div>
            <div className="space-y-1">
              <Label>End time <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Input type="time" value={form.end_time} onChange={set('end_time')} />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Location <span className="text-slate-400 text-xs">(leave blank to use restaurant name)</span></Label>
            <Input value={form.location} onChange={set('location')} placeholder="e.g. Main Hall, Beer Garden…" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Price per ticket (£)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.price_per_ticket}
                onChange={set('price_per_ticket')}
                placeholder="0.00"
              />
              {isFree && (
                <p className="text-xs text-emerald-600 font-medium">Free event — no payment required</p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Total capacity *</Label>
              <Input
                type="number"
                min="1"
                value={form.capacity}
                onChange={set('capacity')}
                placeholder="e.g. 100"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Hero image URL <span className="text-slate-400 text-xs">(optional)</span></Label>
            <Input value={form.image_url} onChange={set('image_url')} placeholder="https://…" />
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label>Terms &amp; Conditions <span className="text-slate-400 text-xs">(optional — shown in confirmation email &amp; booking page)</span></Label>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, terms_and_conditions: TC_TEMPLATE }))}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium underline underline-offset-2"
              >
                Use template
              </button>
            </div>
            <Textarea
              value={form.terms_and_conditions}
              onChange={set('terms_and_conditions')}
              placeholder="e.g. No refunds within 48 hours of the event. Over 18s only. Tickets are non-transferable."
              rows={6}
            />
            {!form.terms_and_conditions && (
              <p className="text-xs text-amber-600">
                Tip: Click "Use template" for a UK-compliant T&amp;C template you can customise.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GuestListDialog({ open, onClose, event, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingIn, setCheckingIn] = useState(null);

  const load = useCallback(async () => {
    if (!event) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/events/${event.id}/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load guest list');
    } finally {
      setLoading(false);
    }
  }, [event, token]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleCheckIn = async (ticketId) => {
    setCheckingIn(ticketId);
    try {
      await axios.post(
        `${API}/admin/events/${event.id}/tickets/${ticketId}/check-in`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket checked in');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check in');
    } finally {
      setCheckingIn(null);
    }
  };

  const totalSold = data?.bookings?.reduce((s, b) => s + (b.ticket_count || 0), 0) || 0;
  const totalCheckedIn = data?.bookings?.flatMap(b => b.tickets || []).filter(t => t.checked_in).length || 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Guest List — {event?.name}
          </DialogTitle>
        </DialogHeader>

        {loading && <p className="text-center text-slate-400 py-8">Loading…</p>}

        {data && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-800">{totalSold}</p>
                <p className="text-xs text-slate-500">Tickets sold</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">{totalCheckedIn}</p>
                <p className="text-xs text-slate-500">Checked in</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-700">
                  {Math.max(0, (event?.capacity || 0) - totalSold)}
                </p>
                <p className="text-xs text-slate-500">Remaining</p>
              </div>
            </div>

            {data.bookings.length === 0 ? (
              <p className="text-center text-slate-400 py-8">No bookings yet</p>
            ) : (
              <div className="space-y-3">
                {data.bookings.map((b) => (
                  <div key={b.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-slate-800">{b.customer_name}</p>
                        <p className="text-sm text-slate-500">{b.customer_email}</p>
                      </div>
                      <div className="text-right">
                        <Badge variant={b.payment_status === 'paid' || b.payment_status === 'free' ? 'default' : 'secondary'}>
                          {b.payment_status === 'free' ? 'Free' : b.payment_status === 'paid' ? 'Paid' : 'Pending'}
                        </Badge>
                        <p className="text-xs text-slate-500 mt-1">{b.ticket_count} ticket{b.ticket_count !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    {/* Per-ticket rows */}
                    <div className="space-y-1 mt-2">
                      {(b.tickets || []).map((t) => (
                        <div key={t.id} className="flex items-center justify-between bg-slate-50 rounded px-3 py-1.5">
                          <div className="flex items-center gap-2">
                            <Ticket className="h-3.5 w-3.5 text-slate-400" />
                            <span className="text-sm text-slate-600">Ticket {t.ticket_number}</span>
                            {t.checked_in && (
                              <span className="text-xs text-emerald-600 font-medium">
                                ✓ Checked in {t.checked_in_at ? format(new Date(t.checked_in_at), 'HH:mm') : ''}
                              </span>
                            )}
                          </div>
                          {!t.checked_in && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-xs"
                              disabled={checkingIn === t.id}
                              onClick={() => handleCheckIn(t.id)}
                            >
                              Check in
                            </Button>
                          )}
                          {t.checked_in && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminEvents() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(res.data);
    } catch (err) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleDelete = async (event) => {
    if (!window.confirm(`Delete "${event.name}"? This cannot be undone.`)) return;
    setDeleting(event.id);
    try {
      await axios.delete(`${API}/admin/events/${event.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Event deleted');
      fetchEvents();
    } catch (err) {
      toast.error('Failed to delete event');
    } finally {
      setDeleting(null);
    }
  };

  const copyLink = (event) => {
    const url = `${window.location.origin}/event/${event.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Booking link copied to clipboard');
  };

  const openCreate = () => { setEditingEvent(null); setFormOpen(true); };
  const openEdit = (e) => { setEditingEvent(e); setFormOpen(true); };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Events</h1>
            <p className="text-sm text-slate-500 mt-1">Create events and manage your guest list</p>
          </div>
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            New Event
          </Button>
        </div>

        {/* Events list */}
        {loading && (
          <div className="text-center text-slate-400 py-16">Loading…</div>
        )}

        {!loading && events.length === 0 && (
          <div className="text-center py-20 border-2 border-dashed border-slate-200 rounded-2xl">
            <CalendarDays className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium mb-1">No events yet</p>
            <p className="text-sm text-slate-400 mb-4">Create your first event to get started</p>
            <Button onClick={openCreate} variant="outline" className="gap-2">
              <Plus className="h-4 w-4" />
              Create Event
            </Button>
          </div>
        )}

        <div className="space-y-4">
          {events.map((ev) => {
            const isFree = ev.price_per_ticket === 0;
            const remaining = Math.max(0, ev.capacity - ev.tickets_sold);
            const soldOut = remaining === 0;
            const isPast = ev.date < format(new Date(), 'yyyy-MM-dd');
            const eventUrl = `${window.location.origin}/event/${ev.id}`;
            const checkInUrl = `/admin/events/${ev.id}/check-in`;

            return (
              <div
                key={ev.id}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm"
              >
                {ev.image_url && (
                  <div
                    className="h-32 bg-cover bg-center"
                    style={{ backgroundImage: `url(${ev.image_url})` }}
                  />
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h2 className="text-lg font-bold text-slate-900 truncate">{ev.name}</h2>
                        {isFree ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0">Free</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700 border-0">£{ev.price_per_ticket?.toFixed(2)}/ticket</Badge>
                        )}
                        {soldOut && <Badge variant="destructive">Sold out</Badge>}
                        {isPast && <Badge variant="secondary">Past</Badge>}
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mb-2">
                        <span className="flex items-center gap-1">
                          <CalendarDays className="h-3.5 w-3.5" />
                          {ev.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {ev.time}{ev.end_time ? ` – ${ev.end_time}` : ''}
                        </span>
                        {ev.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {ev.location}
                          </span>
                        )}
                      </div>

                      {ev.description && (
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3">{ev.description}</p>
                      )}

                      <div className="flex gap-4 text-sm">
                        <span className="flex items-center gap-1 text-slate-600">
                          <Ticket className="h-3.5 w-3.5" />
                          <strong>{ev.tickets_sold}</strong> sold
                        </span>
                        <span className="flex items-center gap-1 text-slate-600">
                          <Users className="h-3.5 w-3.5" />
                          <strong>{remaining}</strong> remaining
                        </span>
                        <span className="text-slate-400">/ {ev.capacity} capacity</span>
                      </div>
                    </div>
                  </div>

                  {/* Booking link preview */}
                  <div className="mt-4 flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-xs text-slate-400 truncate flex-1">{eventUrl}</span>
                    <button
                      onClick={() => copyLink(ev)}
                      className="text-slate-500 hover:text-slate-800 transition-colors"
                      title="Copy link"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <a
                      href={eventUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-500 hover:text-slate-800 transition-colors"
                      title="Open booking page"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => navigate(`/admin/events/${ev.id}/guests`)}
                    >
                      <List className="h-3.5 w-3.5" />
                      Guest list
                    </Button>
                    <a href={checkInUrl} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="gap-1.5">
                        <QrCode className="h-3.5 w-3.5" />
                        Door scanner
                      </Button>
                    </a>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => openEdit(ev)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-red-500 hover:text-red-700 hover:bg-red-50"
                      disabled={deleting === ev.id}
                      onClick={() => handleDelete(ev)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <EventFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initial={editingEvent}
        onSaved={fetchEvents}
      />
    </AdminLayout>
  );
}
